import { lazy, Suspense } from "react"
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom"
import { useTabletStore } from "./stores/tablet-store"
import { AnimatePresence } from "motion/react"
import AppLayout from "./components/layout/app-layout"
import { ProtectedRoute } from "./router/protected-route"
import { PublicRoute } from "./router/public-route"
import { PermissionRoute } from "./router/permission-route"
import { PageTransition } from "./components/shared/page-transition"
import { DashboardSkeleton } from "./pages/dashboard/dashboard-skeleton"

// Lazy loaded pages
const SigninPage = lazy(() => import("./pages/auth/signin-page").then((m) => ({ default: m.SigninPage })))
const SignupPage = lazy(() => import("./pages/auth/signup-page").then((m) => ({ default: m.SignupPage })))
const ForgotPasswordPage = lazy(() => import("./pages/auth/forgot-password-page").then((m) => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import("./pages/auth/reset-password-page").then((m) => ({ default: m.ResetPasswordPage })))
const AcceptInvitationPage = lazy(() => import("./pages/auth/accept-invitation-page").then((m) => ({ default: m.AcceptInvitationPage })))
const TwoFactorPage = lazy(() => import("./pages/auth/two-factor-page").then((m) => ({ default: m.TwoFactorPage })))

const DashboardPage = lazy(() => import("./pages/dashboard/dashboard-page").then((m) => ({ default: m.DashboardPage })))
const ReservationsPage = lazy(() => import("./pages/reservations/reservations-page").then((m) => ({ default: m.ReservationsPage })))
const NewReservationPage = lazy(() => import("./pages/reservations/new-reservation-page").then((m) => ({ default: m.NewReservationPage })))
const ReservationDetailPage = lazy(() => import("./pages/reservations/reservation-detail-page").then((m) => ({ default: m.ReservationDetailPage })))
const AgendaPage = lazy(() => import("./pages/reservations/agenda-page").then((m) => ({ default: m.AgendaPage })))
const RoomsPage = lazy(() => import("./pages/rooms/rooms-page").then((m) => ({ default: m.RoomsPage })))
const RoomDetailPage = lazy(() => import("./pages/rooms/room-detail-page").then((m) => ({ default: m.RoomDetailPage })))
const RoomAvailabilityPage = lazy(() => import("./pages/rooms/room-availability-page").then((m) => ({ default: m.RoomAvailabilityPage })))
const BuildingsPage = lazy(() => import("./pages/buildings/buildings-page").then((m) => ({ default: m.BuildingsPage })))
const BuildingDetailPage = lazy(() => import("./pages/buildings/building-detail-page").then((m) => ({ default: m.BuildingDetailPage })))

const UsersPage = lazy(() => import("./pages/admin/users-page").then((m) => ({ default: m.UsersPage })))
const RolesPage = lazy(() => import("./pages/admin/roles-page").then((m) => ({ default: m.RolesPage })))
const SettingsPage = lazy(() => import("./pages/admin/settings-page").then((m) => ({ default: m.SettingsPage })))
const InvitationsPage = lazy(() => import("./pages/admin/invitations-page").then((m) => ({ default: m.InvitationsPage })))
const InvitationDetailPage = lazy(() => import("./pages/admin/invitation-detail-page").then((m) => ({ default: m.InvitationDetailPage })))
const ActivityPage = lazy(() => import("./pages/admin/activity-page").then((m) => ({ default: m.ActivityPage })))
const ActivityDetailPage = lazy(() => import("./pages/admin/activity-detail-page").then((m) => ({ default: m.ActivityDetailPage })))
const CalendarSyncPage = lazy(() => import("./pages/admin/calendar-sync-page").then((m) => ({ default: m.CalendarSyncPage })))
const EquipmentPage = lazy(() => import("./pages/admin/equipment-page").then((m) => ({ default: m.EquipmentPage })))
const DevicesPage = lazy(() => import("./pages/admin/devices-page").then((m) => ({ default: m.DevicesPage })))
const DeviceDetailPage = lazy(() => import("./pages/admin/device-detail-page").then((m) => ({ default: m.DeviceDetailPage })))

const ProfilePage = lazy(() => import("./pages/profile/profile-page").then((m) => ({ default: m.ProfilePage })))
const NotificationPreferencesPage = lazy(() => import("./pages/profile/notification-preferences-page").then((m) => ({ default: m.NotificationPreferencesPage })))
const SecurityPage = lazy(() => import("./pages/profile/security-page").then((m) => ({ default: m.SecurityPage })))

const HelpPage = lazy(() => import("./pages/help-page").then((m) => ({ default: m.HelpPage })))

const TabletPage = lazy(() => import("./pages/tablet/tablet-page").then((m) => ({ default: m.TabletPage })))
const TabletPairingPage = lazy(() => import("./pages/tablet/tablet-pairing-page").then((m) => ({ default: m.TabletPairingPage })))
const LandingPage = lazy(() => import("./pages/landing/landing-page").then((m) => ({ default: m.LandingPage })))
const ContactPage = lazy(() => import("./pages/contact/contact-page").then((m) => ({ default: m.ContactPage })))
const NotFoundPage = lazy(() => import("./pages/not-found-page").then((m) => ({ default: m.NotFoundPage })))

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
    </div>
  )
}

/**
 * Guard léger pour le mode tablette : si la tablette n'est pas appairée,
 * tout accès à `/tablet` redirige vers l'écran de pairing. Inversement, si
 * elle est déjà appairée et qu'elle arrive sur `/tablet/pairing`, on la
 * renvoie vers le dashboard tablette principal.
 */
function TabletGuard({ children, requirePaired }: { children: React.ReactNode; requirePaired: boolean }) {
  const isPaired = useTabletStore((s) => s.isPaired)

  if (requirePaired && !isPaired) {
    return <Navigate to="/tablet/pairing" replace />
  }

  if (!requirePaired && isPaired) {
    return <Navigate to="/tablet" replace />
  }

  return <>{children}</>
}

function AnimatedRoutes() {
  const location = useLocation()

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Landing page — public */}
        <Route path="/" element={<Suspense><PageTransition><LandingPage /></PageTransition></Suspense>} />
        <Route path="/contact" element={<Suspense><PageTransition><ContactPage /></PageTransition></Suspense>} />

        {/* Public auth routes — redirect to dashboard if already authenticated */}
        <Route element={<PublicRoute />}>
          <Route path="/signin" element={<Suspense><PageTransition><SigninPage /></PageTransition></Suspense>} />
          <Route path="/signup" element={<Suspense><PageTransition><SignupPage /></PageTransition></Suspense>} />
          <Route path="/forgot-password" element={<Suspense><PageTransition><ForgotPasswordPage /></PageTransition></Suspense>} />
          <Route path="/reset-password" element={<Suspense><PageTransition><ResetPasswordPage /></PageTransition></Suspense>} />
          <Route path="/auth/two-factor" element={<Suspense><PageTransition><TwoFactorPage /></PageTransition></Suspense>} />
          <Route path="/invitation/:token" element={<Suspense><PageTransition><AcceptInvitationPage /></PageTransition></Suspense>} />
        </Route>

        {/* Tablet mode — standalone, hors AppLayout */}
        <Route
          path="/tablet/pairing"
          element={
            <TabletGuard requirePaired={false}>
              <Suspense><TabletPairingPage /></Suspense>
            </TabletGuard>
          }
        />
        <Route
          path="/tablet"
          element={
            <TabletGuard requirePaired={true}>
              <Suspense><TabletPage /></Suspense>
            </TabletGuard>
          }
        />

        {/* Protected app routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            {/* Dashboard */}
            <Route path="/dashboard" element={<Suspense fallback={<DashboardSkeleton />}><DashboardPage /></Suspense>} />

            {/* Reservations */}
            <Route path="/reservations" element={<Suspense><ReservationsPage /></Suspense>} />
            <Route path="/reservations/new" element={<Suspense><NewReservationPage /></Suspense>} />
            <Route path="/reservations/:id" element={<Suspense><ReservationDetailPage /></Suspense>} />
            <Route path="/my/reservations" element={<Suspense><ReservationsPage /></Suspense>} />
            <Route path="/agenda" element={<Suspense><AgendaPage /></Suspense>} />

            {/* Rooms */}
            <Route path="/rooms" element={<Suspense><RoomsPage /></Suspense>} />
            <Route path="/rooms/:id" element={<Suspense><RoomDetailPage /></Suspense>} />
            <Route path="/rooms/:id/availability" element={<Suspense><RoomAvailabilityPage /></Suspense>} />

            {/* Buildings */}
            <Route path="/buildings" element={<Suspense><BuildingsPage /></Suspense>} />
            <Route path="/buildings/:id" element={<Suspense><BuildingDetailPage /></Suspense>} />

            {/* Profile */}
            <Route path="/profile" element={<Suspense><ProfilePage /></Suspense>} />
            <Route path="/profile/notifications" element={<Suspense><NotificationPreferencesPage /></Suspense>} />
            <Route path="/profile/security" element={<Suspense><SecurityPage /></Suspense>} />

            {/* Help */}
            <Route path="/help" element={<Suspense><HelpPage /></Suspense>} />

            {/* Calendar sync */}
            <Route path="/calendar-sync" element={<Suspense><CalendarSyncPage /></Suspense>} />

            {/* Admin routes — permission protected */}
            <Route element={<PermissionRoute permission="users.view" />}>
              <Route path="/admin/users" element={<Suspense><UsersPage /></Suspense>} />
            </Route>
            <Route element={<PermissionRoute permission="settings.manage_roles" />}>
              <Route path="/admin/roles" element={<Suspense><RolesPage /></Suspense>} />
            </Route>
            <Route element={<PermissionRoute permission="settings.manage_invitations" />}>
              <Route path="/admin/invitations" element={<Suspense><InvitationsPage /></Suspense>} />
              <Route path="/admin/invitations/:id" element={<Suspense><InvitationDetailPage /></Suspense>} />
            </Route>
            <Route element={<PermissionRoute permission="settings.view" />}>
              <Route path="/admin/settings" element={<Suspense><SettingsPage /></Suspense>} />
            </Route>
            <Route element={<PermissionRoute permission="settings.view_logs" />}>
              <Route path="/admin/activity" element={<Suspense><ActivityPage /></Suspense>} />
              <Route path="/admin/activity/:id" element={<Suspense><ActivityDetailPage /></Suspense>} />
            </Route>
            <Route element={<PermissionRoute permission="equipment.view" />}>
              <Route path="/admin/equipment" element={<Suspense><EquipmentPage /></Suspense>} />
            </Route>
            <Route element={<PermissionRoute permission="devices.view" />}>
              <Route path="/admin/devices" element={<Suspense><DevicesPage /></Suspense>} />
              <Route path="/admin/devices/:id" element={<Suspense><DeviceDetailPage /></Suspense>} />
            </Route>

            {/* 404 inside app layout */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>

        {/* 404 outside app (public) */}
        <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  )
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <AnimatedRoutes />
      </Suspense>
    </BrowserRouter>
  )
}

export default App
